import React, { useState, useEffect } from 'react';
import { ThemeProvider } from '@emotion/react';
import { RouterProvider } from 'react-router-dom';
import { lightTheme, darkTheme } from './theme';
import router from './src/routes';
import './src/i18n';

type ThemeMode = 'light' | 'dark' | 'system';

const App: React.FC = () => {
  // Get the theme from query parameters or fallback to 'system'
  const getQueryTheme = (): ThemeMode => {
    const params = new URLSearchParams(window.location.search);
    const themeParam = params.get('theme') as ThemeMode;
    return themeParam || 'system';
  };

  const [themeMode, setThemeMode] = useState<ThemeMode>(getQueryTheme());
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);

  const applyTheme = (theme: ThemeMode) => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      setIsDarkMode(true);
    } else {
      document.documentElement.classList.remove('dark');
      setIsDarkMode(false);
    }
  };

  
  const detectSystemTheme = () => {
    const systemPreference = window.matchMedia('(prefers-color-scheme: dark)');
    applyTheme(systemPreference.matches ? 'dark' : 'light');

    const systemThemeListener = (e: MediaQueryListEvent) => {
      if (themeMode === 'system') {
        applyTheme(e.matches ? 'dark' : 'light');
      }
    };

    systemPreference.addEventListener('change', systemThemeListener);

    return () => {
      systemPreference.removeEventListener('change', systemThemeListener);
    };
  };

  useEffect(() => {
    if (themeMode === 'system') {
      detectSystemTheme();
    } else {
      applyTheme(themeMode);
    }
  }, [themeMode]);

  const currentTheme = isDarkMode ? darkTheme : lightTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <RouterProvider router={router} />
    </ThemeProvider>
  );
};

export default App;
